import React, { useState } from 'react';
import axios from 'axios';
import { FaTimes } from 'react-icons/fa';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// This is the payment link provided by the client
const PAYMENT_LINK = 'https://your-payment-provider.com/pay';

const PaymentModal = ({ dog, type, onClose }) => {
  const [formData, setFormData] = useState({
    donor_name: '',
    donor_email: '',
    donor_phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const amount = type === 'sponsor' ? dog.sponsor_price_monthly : dog.bowl_price;
  const title = type === 'sponsor' ? `Sponsor ${dog.name}` : `Buy a bowl for ${dog.name}`;
  const frequency = type === 'sponsor' ? 'monthly' : 'one-time';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = type === 'sponsor' ? '/sponsorships' : '/bowl-purchases';
      await axios.post(`${API}${endpoint}`, {
        dog_id: dog.id,
        amount,
        ...formData,
        ...(type === 'sponsor' && { frequency })
      });

      setSuccess(true);
      
      // Redirect to payment link after 1.5 seconds
      setTimeout(() => {
        window.open(PAYMENT_LINK, '_blank');
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error creating payment intent:', error);
      alert('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-2xl max-w-md w-full p-8 relative"
        data-testid="payment-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          data-testid="close-modal-button"
        >
          <FaTimes className="text-2xl" />
        </button>

        {success ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank you!</h3>
            <p className="text-gray-600">Redirecting to payment...</p>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
            <p className="text-gray-600 mb-6">
              Amount: <strong>₾{amount}</strong> {type === 'sponsor' && '/ month'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.donor_name}
                  onChange={(e) => setFormData({ ...formData, donor_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="donor-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.donor_email}
                  onChange={(e) => setFormData({ ...formData, donor_email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="donor-email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone (optional)
                </label>
                <input
                  type="tel"
                  value={formData.donor_phone}
                  onChange={(e) => setFormData({ ...formData, donor_phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="donor-phone-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 disabled:bg-gray-400 transition-colors"
                data-testid="proceed-payment-button"
              >
                {loading ? 'Processing...' : 'Proceed to Payment'}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentModal;
